// src/features/auth/token-management/storage/SecureStorage.ts

import { EncryptionService } from '../../security/storage/EncryptionService';
import { KeyDerivation } from '../../security/storage/KeyDerivation';

export interface SecureStorageConfig {
  storageType?: 'localStorage' | 'sessionStorage';
  encryptionKey?: string;
  keyPrefix?: string;
  useCompression?: boolean;
  algorithm?: 'AES-GCM' | 'AES-CBC';
}

export interface StorageMetadata {
  version: number;
  algorithm: string;
  iv: string;
  salt?: string;
  compressed?: boolean;
  timestamp: number;
}

export class SecureStorage {
  private config: Required<SecureStorageConfig>;
  private storage: Storage;
  private encryptionService: EncryptionService;
  private keyDerivation: KeyDerivation;
  private derivedKey: CryptoKey | null = null;
  private readonly METADATA_SUFFIX = '_meta';
  private readonly VERSION = 1;

  constructor(config: SecureStorageConfig = {}) {
    this.config = {
      storageType: 'localStorage',
      encryptionKey: '',
      keyPrefix: 'secure_',
      useCompression: false,
      algorithm: 'AES-GCM',
      ...config,
    };

    // Initialize storage
    this.storage = this.config.storageType === 'localStorage' 
      ? localStorage 
      : sessionStorage;

    // Initialize encryption services
    this.encryptionService = new EncryptionService({
      algorithm: this.config.algorithm,
    });
    this.keyDerivation = new KeyDerivation();
  }

  /**
   * Set encrypted value
   */
  async setSecure(key: string, value: any): Promise<void> {
    try {
      // Serialize value
      const serialized = JSON.stringify(value);
      
      // Compress if enabled
      const data = this.config.useCompression 
        ? await this.compress(serialized)
        : serialized;

      // Get or derive encryption key
      const cryptoKey = await this.getOrDeriveKey();

      // Encrypt data
      const encrypted = await this.encryptionService.encrypt(data, cryptoKey);

      // Create metadata
      const metadata: StorageMetadata = {
        version: this.VERSION,
        algorithm: this.config.algorithm,
        iv: encrypted.iv,
        salt: encrypted.salt,
        compressed: this.config.useCompression,
        timestamp: Date.now(),
      };

      // Store encrypted data and metadata
      const storageKey = this.getStorageKey(key);
      this.storage.setItem(storageKey, encrypted.ciphertext);
      this.storage.setItem(
        `${storageKey}${this.METADATA_SUFFIX}`,
        JSON.stringify(metadata)
      );
    } catch (error) {
      console.error('SecureStorage: Failed to encrypt and store data', error);
      throw new Error('Failed to store secure data');
    }
  }

  /**
   * Get decrypted value
   */
  async getSecure(key: string): Promise<any> {
    try {
      const storageKey = this.getStorageKey(key);
      
      // Get encrypted data
      const encryptedData = this.storage.getItem(storageKey);
      if (!encryptedData) {
        return null;
      }

      // Get metadata
      const metadataStr = this.storage.getItem(`${storageKey}${this.METADATA_SUFFIX}`);
      if (!metadataStr) {
        throw new Error('Missing encryption metadata');
      }

      const metadata: StorageMetadata = JSON.parse(metadataStr);

      // Check version compatibility
      if (metadata.version > this.VERSION) {
        throw new Error('Incompatible storage version');
      }

      // Get or derive encryption key
      const cryptoKey = await this.getOrDeriveKey();

      // Decrypt data
      const decrypted = await this.encryptionService.decrypt(
        {
          ciphertext: encryptedData,
          iv: metadata.iv,
          salt: metadata.salt,
        },
        cryptoKey
      );

      // Decompress if needed
      const data = metadata.compressed 
        ? await this.decompress(decrypted)
        : decrypted;

      // Parse and return
      return JSON.parse(data);
    } catch (error) {
      console.error('SecureStorage: Failed to decrypt data', error);
      return null;
    }
  }

  /**
   * Remove secure item
   */
  removeSecure(key: string): void {
    const storageKey = this.getStorageKey(key);
    this.storage.removeItem(storageKey);
    this.storage.removeItem(`${storageKey}${this.METADATA_SUFFIX}`);
  }

  /**
   * Check if secure item exists
   */
  hasSecure(key: string): boolean {
    const storageKey = this.getStorageKey(key);
    return this.storage.getItem(storageKey) !== null;
  }

  /**
   * Clear all secure items
   */
  clearSecure(): void {
    const keys = this.getAllSecureKeys();
    keys.forEach(key => {
      this.storage.removeItem(key);
      this.storage.removeItem(`${key}${this.METADATA_SUFFIX}`);
    });

    // Clear derived key
    this.derivedKey = null;
  }

  /**
   * Get all secure keys
   */
  getAllSecureKeys(): string[] {
    const keys: string[] = [];
    
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i);
      if (key && key.startsWith(this.config.keyPrefix) && !key.endsWith(this.METADATA_SUFFIX)) {
        keys.push(key);
      }
    }

    return keys;
  }

  /**
   * Get storage statistics
   */
  getStats(): {
    itemCount: number;
    totalSize: number;
    oldestItem?: { key: string; age: number };
    algorithm: string;
    compressed: boolean;
  } {
    const keys = this.getAllSecureKeys();
    let totalSize = 0;
    let oldestItem: { key: string; age: number } | undefined;
    const now = Date.now();

    keys.forEach(key => {
      const data = this.storage.getItem(key);
      const metadataStr = this.storage.getItem(`${key}${this.METADATA_SUFFIX}`);
      
      if (data && metadataStr) {
        totalSize += data.length + metadataStr.length;
        
        try {
          const metadata: StorageMetadata = JSON.parse(metadataStr);
          const age = now - metadata.timestamp;
          
          if (!oldestItem || age > oldestItem.age) {
            oldestItem = { key: key.replace(this.config.keyPrefix, ''), age };
          }
        } catch {
          // Ignore parse errors
        }
      }
    });

    return {
      itemCount: keys.length,
      totalSize,
      oldestItem,
      algorithm: this.config.algorithm,
      compressed: this.config.useCompression,
    };
  }

  /**
   * Migrate data to new encryption key
   */
  async migrateToNewKey(newKey: string): Promise<void> {
    const keys = this.getAllSecureKeys();
    const oldData: Array<{ key: string; value: any }> = [];

    // Read all data with old key
    for (const storageKey of keys) {
      const key = storageKey.replace(this.config.keyPrefix, '');
      const value = await this.getSecure(key);
      if (value !== null) {
        oldData.push({ key, value });
      }
    }

    // Update encryption key
    this.config.encryptionKey = newKey;
    this.derivedKey = null;

    // Re-encrypt with new key
    for (const { key, value } of oldData) {
      await this.setSecure(key, value);
    }
  }

  /**
   * Export all secure data (decrypted)
   */
  async exportData(): Promise<Record<string, any>> {
    const keys = this.getAllSecureKeys();
    const data: Record<string, any> = {};

    for (const storageKey of keys) {
      const key = storageKey.replace(this.config.keyPrefix, '');
      const value = await this.getSecure(key);
      if (value !== null) {
        data[key] = value;
      }
    }

    return data;
  }

  /**
   * Import data (will be encrypted)
   */
  async importData(data: Record<string, any>): Promise<void> {
    for (const [key, value] of Object.entries(data)) {
      await this.setSecure(key, value);
    }
  }

  /**
   * Get or derive encryption key
   */
  private async getOrDeriveKey(): Promise<CryptoKey> {
    if (this.derivedKey) {
      return this.derivedKey;
    }

    // Use provided key or derive from a master key
    if (this.config.encryptionKey) {
      this.derivedKey = await this.keyDerivation.deriveKey(
        this.config.encryptionKey,
        'storage-encryption'
      );
    } else {
      // Generate a random key if none provided
      this.derivedKey = await this.encryptionService.generateKey();
    }

    return this.derivedKey;
  }

  /**
   * Get storage key with prefix
   */
  private getStorageKey(key: string): string {
    return `${this.config.keyPrefix}${key}`;
  }

  /**
   * Compress data (simple implementation - would use pako or similar in production)
   */
  private async compress(data: string): Promise<string> {
    // This is a placeholder - in production, use a compression library
    return data;
  }

  /**
   * Decompress data
   */
  private async decompress(data: string): Promise<string> {
    // This is a placeholder - in production, use a compression library
    return data;
  }

  /**
   * Check storage availability
   */
  static isAvailable(): boolean {
    try {
      const test = '__secure_storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get storage quota info
   */
  static async getQuotaInfo(): Promise<{
    usage: number;
    quota: number;
    percentUsed: number;
  }> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      return {
        usage: estimate.usage || 0,
        quota: estimate.quota || 0,
        percentUsed: estimate.quota ? ((estimate.usage || 0) / estimate.quota) * 100 : 0,
      };
    }

    // Fallback for older browsers
    return {
      usage: 0,
      quota: 0,
      percentUsed: 0,
    };
  }
}